/*
 * hyperion-index:begin
 * ---
 * path: hyperion-fe/src/platform/api-client/client.ts
 * layer: web
 * area: api
 * kind: api
 * web_root: api
 * relpath: web/src/platform/api-client/client.ts
 * filename: client.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { z, type ZodType } from 'zod';
import {
  captureError,
  configureErrorTelemetryTransport,
} from '@/platform/observability/error-telemetry';
import {
  captureApiFailure,
  captureApiResponse,
  markApiRequest,
} from '@/platform/observability/api-observability';
import {
  debugApiError,
  debugApiRequest,
  debugApiResponse,
} from '@/platform/observability/debug-logger';
import { ERROR_MESSAGES } from '@/platform/api-client/errorCodes';
import { handleLicenseError } from '@/platform/api-client/licenseGate';
import { AuthFailureCircuit, clearBrowserAuthSession } from '@/platform/auth/authFailureCircuit';
import {
  applyResponseKeyContract,
  STRUCTURED_RESPONSE_KEYS,
  WIRE_RESPONSE_KEYS,
} from '@/platform/api-client/responseKeyContract';

/**
 * Pull the error code from either response shape: auth handler
 * {"error": "mfa_required"} or gateway {"error": {"code": "mfa_required"}}.
 */
function errorCodeOf(body: unknown): { data?: Record<string, unknown>; code?: string } {
  const parsedData = z.record(z.string(), z.unknown()).safeParse(body);
  const data = parsedData.success ? parsedData.data : undefined;
  const errorField = data?.error;
  let code: string | undefined;
  if (typeof errorField === 'string') {
    code = errorField;
  } else if (typeof errorField === 'object' && errorField !== null) {
    const nested = (errorField as Record<string, unknown>).code;
    code = typeof nested === 'string' ? nested : undefined;
  }
  return { data, code };
}

// Extend axios config to track retry attempts
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// ── Token refresh queue ─────────────────────────────────────────────────────
// Prevents multiple simultaneous 401 responses from each triggering their own
// token refresh. The first 401 starts the refresh; subsequent 401s queue behind
// the same promise and replay once the new token is available.
let isRefreshing = false;
type QueueEntry = { resolve: () => void; reject: (error: Error) => void };
let failedQueue: QueueEntry[] = [];

// ── Refresh failure cooldown ────────────────────────────────────────────────
// After a refresh fails, block further refresh attempts for a cooldown period
// to prevent hammering the server in a loop.
let lastRefreshFailure = 0;
const REFRESH_COOLDOWN_MS = 10_000; // 10 seconds after a refresh failure

// ── Auth failure circuit breaker ───────────────────────────────────────────
// A systemic 403 storm must converge to one logout, not keep remounting the
// application and replaying every provider/query request indefinitely.
const authFailureCircuit = new AuthFailureCircuit();
let authCircuitLogoutStarted = false;

function openAuthFailureCircuit(failures: number, distinctPaths: number): void {
  if (authCircuitLogoutStarted) return;
  authCircuitLogoutStarted = true;

  captureError('Authentication failure circuit breaker opened', {
    failures,
    distinctPaths,
    windowMs: 10_000,
  });

  localStorage.removeItem('current_tenant_id');
  window.dispatchEvent(new CustomEvent('auth-session-expired'));

  // Bypass this axios instance so cookie cleanup cannot recurse through the
  // same interceptors. This is deliberately not /auth/logout: the circuit is
  // not a user decision and must not revoke a still-refreshable server session.
  void clearBrowserAuthSession().catch(() => undefined);
}

function processQueue(error: Error | null, success: boolean): void {
  for (const entry of failedQueue) {
    if (success) {
      entry.resolve();
    } else {
      entry.reject(error ?? new Error('Token refresh failed'));
    }
  }
  failedQueue = [];
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true, // Send HttpOnly cookies with every request
});

configureErrorTelemetryTransport(async (path, body) => {
  await api.post(path, body);
});

// Request interceptor for tenant context + debug logging.
// Auth is via HttpOnly cookies (sent automatically with withCredentials: true).
// NOTE: No request body transformation — Go backend json tags are inconsistent (some camelCase,
// some snake_case). Each API call sends keys matching its backend struct tags directly.
api.interceptors.request.use(
  (config) => {
    // Add tenant context if available
    // Storage can be absent or throw (private mode, blocked site data,
    // non-browser runtimes); a missing tenant hint must not fail the request.
    let tenantId: string | null = null;
    try {
      tenantId =
        typeof localStorage?.getItem === 'function'
          ? localStorage.getItem('current_tenant_id')
          : null;
    } catch {
      tenantId = null;
    }
    if (tenantId && !config.headers.has('X-Tenant-ID')) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    // Debug logging — capture every outgoing request
    try {
      config._debugStartTime = Date.now();
      debugApiRequest(config.method || 'GET', config.url || '?', config.data);
    } catch {
      /* debug logger unavailable */
    }

    markApiRequest(config);

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: transform snake_case → camelCase, then handle token refresh
api.interceptors.response.use(
  (response) => {
    captureApiResponse(response, 'api');
    // Capture license warning header for the banner component
    const licenseWarning = response.headers?.['x-license-warning'];
    if (licenseWarning) {
      sessionStorage.setItem('license-warning-message', licenseWarning);
      sessionStorage.removeItem('license-warning-dismissed');
      // Notify banner components that a warning has arrived
      window.dispatchEvent(new CustomEvent('license-warning', { detail: licenseWarning }));
    }

    // Response fields are normalized under an explicit contract. API modules
    // declare identifier-keyed maps and opaque JSON; only structured fields are
    // camel-cased. Blob/arraybuffer downloads never enter the JSON boundary.
    if (
      response.data &&
      typeof response.data === 'object' &&
      response.config.responseType !== 'blob' &&
      response.config.responseType !== 'arraybuffer'
    ) {
      response.data = applyResponseKeyContract(
        response.data,
        response.config.responseKeyContract ?? STRUCTURED_RESPONSE_KEYS
      );
    }

    // Debug logging — capture every response
    try {
      const duration = response.config._debugStartTime
        ? Date.now() - response.config._debugStartTime
        : undefined;
      debugApiResponse(
        response.config.method || 'GET',
        response.config.url || '?',
        response.status,
        response.data,
        duration
      );
    } catch {
      /* debug logger unavailable */
    }

    return response;
  },
  async (error: AxiosError) => {
    // Debug logging — capture every API error
    try {
      debugApiError(
        error.config?.method || '?',
        error.config?.url || '?',
        error.response?.status || error.code || 'NETWORK',
        error.response?.data || error.message
      );
    } catch {
      /* debug logger unavailable */
    }

    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Licence outcomes (403 blocked / read-only, 503 authority unavailable)
    // are not authentication failures: route them before the auth-failure
    // circuit can count them and sign the user out.
    if (error.response?.status === 403 || error.response?.status === 503) {
      const { data, code } = errorCodeOf(error.response.data);
      if (handleLicenseError(error.response.status, data, code)) {
        return Promise.reject(error);
      }
    }

    // Handle MFA-related 403 responses
    if (error.response?.status === 403) {
      const { code } = errorCodeOf(error.response.data);
      if (code === 'mfa_required' || code === 'mfa_expired') {
        // Let AuthProvider + ProtectedRoute perform one client-side transition.
        // A hard reload remounts TenantProvider and every query, which was the
        // self-reinforcing production loop behind hyp-3hgg.
        window.dispatchEvent(new CustomEvent('auth-mfa-required', { detail: { code } }));
        return Promise.reject(error);
      }
      const url = originalRequest?.url ?? 'unknown';
      const circuit = authFailureCircuit.record(url);
      if (circuit.justOpened) {
        openAuthFailureCircuit(circuit.failures, circuit.distinctPaths);
      }
    }

    // Skip refresh for auth-check endpoints — 401 just means "not logged in"
    const isAuthEndpoint = originalRequest?.url?.match(/^\/auth\//);

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      // Check cooldown — don't retry refresh if one just failed
      if (Date.now() - lastRefreshFailure < REFRESH_COOLDOWN_MS) {
        // Signal auth expiry to React context (no hard reload)
        window.dispatchEvent(new CustomEvent('auth-session-expired'));
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If a refresh is already in progress, queue this request behind it
      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          // Cookie is already updated by the refresh response — just replay
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        // Attempt token refresh — cookie sent automatically, backend sets new cookie
        await api.post('/auth/refresh', {}, {
          baseURL: '',
          _retry: true,
        } as ExtendedAxiosRequestConfig);

        // Replay all queued requests (cookies are already updated)
        processQueue(null, true);
        lastRefreshFailure = 0; // Reset on success

        // Replay the failed request (cookie is already set)
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed — reject all queued requests and set cooldown
        lastRefreshFailure = Date.now();
        processQueue(toError(refreshError), false);

        // Signal auth expiry to React context instead of hard reload.
        // AuthProvider listens for this and sets isAuthenticated=false,
        // which makes ProtectedRoute redirect to /login via React Router
        // (no full page reload = no remount loop).
        localStorage.removeItem('current_tenant_id');
        window.dispatchEvent(new CustomEvent('auth-session-expired'));
        return Promise.reject(toError(refreshError, 'Token refresh failed'));
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Error telemetry interceptor — all non-401 4xx, 5xx and network errors pass
// through the same fail-safe boundary as the auth client.
api.interceptors.response.use(undefined, (error: AxiosError) => {
  captureApiFailure(error, 'api');
  return Promise.reject(error);
});

// API type definitions
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ApiError {
  code: string;
  error: string;
  details?: Record<string, unknown>;
}

const apiErrorSchema = z.object({
  code: z.string().min(1),
  error: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

const legacyApiErrorSchema = z.object({
  error: z.string(),
});

const legacyMessageErrorSchema = z.object({
  message: z.string(),
});

/** An API response did not match the endpoint's declared runtime contract. */
export class ApiContractError extends Error {
  readonly code = 'INVALID_API_RESPONSE';
  readonly contractCause?: unknown;

  constructor(label: string, cause?: unknown) {
    super(`${label} response did not match its contract`);
    this.name = 'ApiContractError';
    this.contractCause = cause;
  }
}

class UnknownRejectionError extends Error {
  constructor(
    message: string,
    readonly reason: unknown
  ) {
    super(message);
    this.name = 'UnknownRejectionError';
  }
}

// ---------------------------------------------------------------------------
// List response unwrapping
// ---------------------------------------------------------------------------
// The Go backend wraps ALL list endpoints in {data: [...], total: N} via
// httputil.NewListResponse(). This helper unwraps the envelope so frontend
// code gets a plain array. Use for any endpoint that returns an array.

function envelopeData(body: unknown): unknown {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) {
    return body.data;
  }
  return body;
}

type FieldKind = 'string' | 'number' | 'boolean';

/**
 * Minimal runtime item contract for list rows: the value must be an object and
 * each named identity field must have the given primitive type. Keeps the
 * declared TypeScript row type. Use it where a full zod schema is not yet
 * written; it still turns envelope drift and wrong-shape rows into an
 * ApiContractError instead of an empty or garbage list.
 */
export function rowShape<T>(fields: Partial<Record<keyof T & string, FieldKind>>): ZodType<T> {
  const entries = Object.entries(fields) as Array<[string, FieldKind]>;
  return z.custom<T>(
    (value) =>
      Boolean(value) &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      entries.every(([key, kind]) => typeof (value as Record<string, unknown>)[key] === kind),
    { message: `expected a row with ${entries.map(([key, kind]) => `${key}: ${kind}`).join(', ')}` }
  );
}

/**
 * A list-valued FIELD of an API object (not the response envelope). JSON
 * `null`/absent means a Go nil slice or an omitempty field, i.e. no entries;
 * anything else that is not an array is contract drift and throws instead of
 * silently becoming `[]`. Prefer this over an Array.isArray fallback to `[]`.
 */
export function nilableList<T = unknown>(value: unknown, label: string): T[] {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value as T[];
  throw new ApiContractError(label, `expected a list, got ${typeof value}`);
}

/** Options for {@link unwrapList} / {@link unwrapPage}. */
export interface ListEnvelopeOptions {
  /**
   * The endpoint's named list key, for handlers that return e.g.
   * `{alerts: [...]}` or `{policies: [...]}` instead of the canonical
   * `{data: [...]}`. Use the post-casing (camelCase) key. When set, the key
   * MUST be present; a missing key is contract drift, never an empty list.
   */
  key?: string;
}

const LIST_ENVELOPE_KEYS = ['data', 'items'] as const;

/**
 * Resolve the list inside a response body. One place owns the backend's
 * envelope variants so screens never coerce a mismatched shape to `[]`:
 *   - a bare array;
 *   - the canonical `{data: [...]}` (httputil.NewListResponse);
 *   - `{items: [...]}`;
 *   - a named key (`options.key`).
 * JSON `null` (bare, or at a present key) is a Go nil slice and means zero
 * rows. A missing key, a non-list/non-object body (string, number, HTML),
 * or an ambiguous body carrying both `data` and `items` lists fails closed.
 */
function listEnvelope(body: unknown, label: string, options?: ListEnvelopeOptions): unknown {
  if (Array.isArray(body)) {
    if (options?.key) throw new ApiContractError(label, `expected {${options.key}: [...]}`);
    return body;
  }
  // A bare JSON null is how Go encodes a nil slice from a handler that writes
  // the slice directly (e.g. ListScripts): zero rows, not drift.
  if (body === null && !options?.key) return [];
  if (!body || typeof body !== 'object') {
    throw new ApiContractError(label, 'expected a list response');
  }
  const record = body as Record<string, unknown>;
  if (options?.key) {
    // `{data: {<key>: [...]}}` is the canonical envelope around a named list.
    const holder =
      options.key in record
        ? record
        : record.data && typeof record.data === 'object' && !Array.isArray(record.data)
          ? (record.data as Record<string, unknown>)
          : undefined;
    if (!holder || !(options.key in holder)) {
      throw new ApiContractError(label, `missing list key "${options.key}"`);
    }
    return holder[options.key] === null ? [] : holder[options.key];
  }
  const present = LIST_ENVELOPE_KEYS.filter(
    (key) => key in record && (Array.isArray(record[key]) || record[key] === null)
  );
  if (present.length > 1) {
    throw new ApiContractError(label, 'ambiguous list envelope: both data and items present');
  }
  if (present.length === 0) {
    // Not a recognised envelope: let the schema report what actually arrived.
    return 'data' in record ? record.data : record;
  }
  const value = record[present[0]];
  return value === null ? [] : value;
}

/**
 * Unwrap and validate a list response. Malformed payloads fail closed instead
 * of being disguised as a legitimate empty result.
 */
export function unwrapList<T>(
  body: unknown,
  itemSchema: ZodType<T>,
  label = 'API list',
  options?: ListEnvelopeOptions
): T[] {
  const parsed = z.array(itemSchema).safeParse(listEnvelope(body, label, options));
  if (!parsed.success) {
    throw new ApiContractError(label, parsed.error);
  }
  return parsed.data;
}

type IsAny<B> = 0 extends 1 & B ? true : false;
type ElemOf<D> =
  IsAny<D> extends true ? any : NonNullable<D> extends readonly (infer E)[] ? E : unknown;
/** Row type of a list response body: a bare array, or the `data` / `items` list of an envelope. */
export type RowsOf<B> =
  IsAny<B> extends true
    ? any
    : NonNullable<B> extends readonly (infer E)[]
      ? E
      : NonNullable<B> extends { data?: infer D }
        ? ElemOf<D>
        : NonNullable<B> extends { items?: infer I }
          ? ElemOf<I>
          : unknown;

/**
 * unwrapList for endpoints whose row type is already trusted by the caller's
 * TypeScript contract: the envelope is still verified (a wrong shape, HTML or
 * an error body throws ApiContractError) but rows are not re-validated. Use
 * instead of coercing a missing list to an empty array. The row type is taken
 * from the body type (bare array, `data`/`items` envelope, or the named
 * `options.key` list).
 */
export function unwrapRows<B, K extends keyof NonNullable<B> & string>(
  body: B,
  label: string,
  options: { key: K }
): ElemOf<NonNullable<B>[K]>[];
export function unwrapRows<B>(body: B, label?: string): RowsOf<B>[];
export function unwrapRows(
  body: unknown,
  label = 'API list',
  options?: ListEnvelopeOptions
): unknown[] {
  return unwrapList<unknown>(
    body,
    z.custom<unknown>((value) => value !== undefined),
    label,
    options
  );
}

/** Validate a paginated list response while preserving its verified total. */
export function unwrapPage<T>(
  body: unknown,
  itemSchema: ZodType<T>,
  label = 'API list',
  options?: ListEnvelopeOptions
): { data: T[]; total: number } {
  const data = unwrapList(body, itemSchema, label, options);
  if (Array.isArray(body)) return { data, total: data.length };

  const total = z
    .number()
    .int()
    .nonnegative()
    .optional()
    .safeParse((body as Record<string, unknown>).total);
  if (!total.success) {
    throw new ApiContractError(label, total.error);
  }
  return { data, total: total.data ?? data.length };
}

/** Validate a single-object response, accepting either a bare object or `{data: object}`. */
export function unwrapOne<T>(body: unknown, schema: ZodType<T>, label = 'API object'): T {
  const direct = schema.safeParse(body);
  if (direct.success) return direct.data;

  const parsed = schema.safeParse(envelopeData(body));
  if (!parsed.success) {
    throw new ApiContractError(label, direct.error ?? parsed.error);
  }
  return parsed.data;
}

// Helper functions
export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && apiErrorSchema.safeParse(error.response?.data).success;
}

/**
 * The user-facing reason for a failure. Prefers the server's coded/legacy
 * error body over axios' "Request failed with status code 409", then a plain
 * Error's message. The second argument is either per-code overrides or the
 * fallback copy used when the rejection carries no message at all.
 */
export function getErrorMessage(
  error: unknown,
  overridesOrFallback?: Record<string, string> | string
): string {
  const overrides = typeof overridesOrFallback === 'string' ? undefined : overridesOrFallback;
  const fallback =
    typeof overridesOrFallback === 'string' ? overridesOrFallback : 'Something went wrong';
  if (axios.isAxiosError(error)) {
    const data: unknown = error.response?.data;
    const coded = apiErrorSchema.safeParse(data);
    if (coded.success) {
      const { code } = coded.data;
      if (overrides?.[code]) return overrides[code];
      if (ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
      if (coded.data.error) return coded.data.error;
    }
    const legacy = legacyApiErrorSchema.safeParse(data);
    if (legacy.success && legacy.data.error) return legacy.data.error;
    const message = legacyMessageErrorSchema.safeParse(data);
    if (message.success && message.data.message) return message.data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Convert arbitrary rejection reasons into real Error instances with stacks. */
export function toError(value: unknown, fallback = 'Something went wrong'): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string' && value.trim()) return new Error(value);
  return new UnknownRejectionError(fallback, value);
}

// ---------------------------------------------------------------------------
// Device snapshot + file browser helpers
// ---------------------------------------------------------------------------

export function getDeviceSnapshot(deviceId: string, type: string) {
  return api.get<{ snapshotType: string; collectedAt: string; data: unknown }>(
    `/devices/${deviceId}/snapshots/${type}`
  );
}

export function getDeviceScheduledTasks(deviceId: string) {
  return api.get<
    Array<{
      name: string;
      path: string;
      status: string;
      trigger: string;
      nextRun?: string;
      lastRun?: string;
      lastResult?: number;
    }>
  >(`/devices/${deviceId}/scheduled-tasks`);
}

export interface WakeRequest {
  id: string;
  tenant_id: string;
  customer_id: string;
  device_id: string;
  network_monitoring_agent_id: string;
  status: 'queued' | 'completed' | 'failed';
  error?: string;
  result?: Array<{
    device_id: string;
    mac_address?: string;
    broadcast_address: string;
    success: boolean;
    error?: string;
  }>;
  created_at: string;
  completed_at?: string;
}

export function wakeDevice(deviceId: string) {
  return api.post<{ success: boolean; message: string; request: WakeRequest }>(
    `/devices/${deviceId}/wake`,
    undefined,
    { responseKeyContract: WIRE_RESPONSE_KEYS }
  );
}

export function getWakeRequest(deviceId: string, requestId: string) {
  return api.get<WakeRequest>(`/devices/${deviceId}/wake/${requestId}`, {
    responseKeyContract: WIRE_RESPONSE_KEYS,
  });
}

// ---------------------------------------------------------------------------
// OneDrive cache repair
// ---------------------------------------------------------------------------

export interface RepairOneDriveRequest {
  user_sid?: string;
  force?: boolean;
  wait_secs?: number;
}

export interface RepairOneDriveResponse {
  command_id: string;
  device_id: string;
  status: string;
}

export async function repairOneDriveCache(
  deviceId: string,
  body: RepairOneDriveRequest = {}
): Promise<RepairOneDriveResponse> {
  const response = await api.post<RepairOneDriveResponse>(
    `/devices/${deviceId}/repair-onedrive`,
    body,
    { responseKeyContract: WIRE_RESPONSE_KEYS }
  );
  return response.data;
}
